import { renderCell, renderHeader } from '../components/table';
import { useMemo, useContext } from 'react';
import { Flex, Box, Center, Spinner } from '@chakra-ui/react';
import { Wrapper } from '../components/Wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';
import { BreakdownBy } from '../components/breakdown-by';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectHireTable,
  selectHireTableLoading,
  selectHireReportsGroup,
} from '@/store/atsReports/hire/selectors';
import { applyGroup } from '@/store/atsReports/hire';
import { secondsToHumanTime } from '@/utils/secondsToHumanTime';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';

export interface Option {
  label: string;
  id: number | string;
}

const groupBy = [
  { id: null, label: 'Time', key: 'dt' },
  { id: 'job_name', label: 'Job name', key: 'g1.name' },
  { id: 'primary_recruiter', label: 'Primary recruiter', key: 'g1.name' },
  { id: 'primary_hm', label: 'Primary hiring manager', key: 'g1.name' },
  {
    id: 'appl_contact_source',
    label: 'Source (on application)',
    key: 'g1?.name',
  },
  { id: 'department', label: 'Department', key: 'g1.name' },
  {
    id: 'appl_credited_to',
    label: 'Credited to (on application)',
    key: 'g1.name',
  },
];

const breakDownBy = [
  { id: null, label: 'None' },
  { id: 'job_name', label: 'Job name' },
  { id: 'primary_recruiter', label: 'Primary recruiter' },
  { id: 'primary_hm', label: 'Primary hiring manager' },
  { id: 'appl_contact_source', label: 'Source (on application)' },
  { id: 'department', label: 'Department' },
  { id: 'appl_credited_to', label: 'Credited to (on application)' },
];

export const HireTable = ({
  groupBy1 = undefined,
  groupBy2 = undefined,
  customData = undefined,
  isReadOnly = false,
  isPrintView = false,
  customBreakdownChange = undefined,
  renderTitle = undefined,
  onDrillDownOpen = (_data) => {},
  isStaticReport = false,
  onSortChange = (_sortState) => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  feReportsV3 = false,
  isFullScreen = false,
  isFilterApplied = false,
}) => {
  const dispatch = useDispatch();
  const { consumer } = useContext(ReportSelectionContext);
  const reduxIsLoading = useSelector(selectHireTableLoading);
  const reduxGroup = useSelector(selectHireReportsGroup);
  const reduxHiresTable = useSelector(selectHireTable);

  const isHiresTableLoading = feReportsV3 ? false : reduxIsLoading;
  const group = feReportsV3 ? null : reduxGroup;
  const hiresTable = feReportsV3 ? [] : reduxHiresTable;

  const dataToUse = customData || hiresTable;
  const isDataEmpty = isReportTableEmpty(dataToUse, 'hires');
  const { handleDrillDown } = useDrillDownHandler(true, onDrillDownOpen);
  const getGroupValue = (value: any, fallbackValue: any) =>
    feReportsV3 ? value : value || fallbackValue;

  const drilldownGroup = {
    group_by_1: getGroupValue(groupBy1, group?.group_by_1),
    group_by_2: getGroupValue(groupBy2, group?.group_by_2),
  };

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- pre-existing; unrelated to flag removal
  const columns = useMemo(() => {
    const header = feReportsV3
      ? groupBy1 || groupBy[0]
      : groupBy1 || group?.group_by_1 || groupBy[0];
    return [
      {
        ...renderHeader({
          accessor: 'header',
          title: header?.label || 'Name',
          width: 300,
          expand: false,
          sort: true,
          isReadOnly,
          groupBy: group?.group_by_1?.id,
          breakDownBy: group?.group_by_2?.id,
          renderFooter: () => <KText size="sm-semi-bold">Total</KText>,
        }),
        id: 'header',
        title: header?.label || 'Name',
        columnConfig: false,
      },
      {
        ...renderCell({
          title: 'Hires',
          accessor: 'hires',
          width: 100,
          sort: true,
          render: (_, row) => {
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={'pointer'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Hires',
                    false,
                    drilldownGroup,
                    null,
                    row?.hires
                  )
                }
              >
                {row?.hires}
              </Flex>
            );
          },
          renderFooter: (info) => {
            const row = info?.rows?.[0]?.original;
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={'pointer'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Hires',
                    true,
                    drilldownGroup,
                    null,
                    row?.hires_total
                  )
                }
              >
                <KText size="sm-semi-bold">{row?.hires_total}</KText>
              </Flex>
            );
          },
        }),
        id: 'hires',
        title: 'Hires',
        columnConfig: true,
      },
      {
        ...renderCell({
          title: 'Avg. time to hire',
          accessor: 'time_to_hire',
          width: 100,
          sort: true,
          render: (_, row) => {
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={'pointer'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Time to hire',
                    false,
                    drilldownGroup,
                    null,
                    secondsToHumanTime(row?.time_to_hire / row?.hires)
                  )
                }
              >
                {secondsToHumanTime(row?.time_to_hire / row?.hires)}
              </Flex>
            );
          },
        }),
        id: 'time_to_hire',
        title: 'Avg. time to hire',
        columnConfig: true,
        sortType: (rowA, rowB) => {
          const hiresA = parseInt(rowA.original?.hires) || 0;
          const hiresB = parseInt(rowB.original?.hires) || 0;
          const avgA =
            hiresA > 0 ? parseInt(rowA.values?.time_to_hire || 0) / hiresA : 0;
          const avgB =
            hiresB > 0 ? parseInt(rowB.values?.time_to_hire || 0) / hiresB : 0;
          return avgA - avgB;
        },
      },
    ];
  }, [groupBy1, groupBy2, feReportsV3, group]); //eslint-disable-line

  const handleBreakdownChange = (key: string, option: Option) => {
    switch (key) {
      case 'group_by_1':
        dispatch(applyGroup({ key: 'group_by_1', value: option }));

        break;
      case 'group_by_2':
        dispatch(applyGroup({ key: 'group_by_2', value: option }));
        break;
      default:
        break;
    }
  };

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: groupBy,
      selected: feReportsV3
        ? groupBy1 || groupBy[0]
        : groupBy1 || group?.group_by_1 || groupBy[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: breakDownBy,
      selected: feReportsV3
        ? groupBy2 || breakDownBy[0]
        : groupBy2 || group?.group_by_2 || breakDownBy[0],
    },
  ];

  if (isFilterApplied) {
    return (
      <EmptyReportChart
        text="Filters not applicable"
        subText="The applied filters are not applicable for this block"
      />
    );
  }

  return (
    <Wrapper role="group" isReadOnly={isReadOnly || isStaticReport}>
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle ? (
            renderTitle?.()
          ) : (
            <Box flex="1">
              <TitleWithTooltip
                label="Hire breakdown"
                metricDefinition="Comprehensive breakdown of the hiring data"
              />
            </Box>
          )}
          <Flex
            gap="8px"
            mr={
              isPrintView
                ? '0'
                : isDataEmpty
                  ? '35px'
                  : isEditMode
                    ? '55px'
                    : '35px'
            }
            mb="12px"
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  customBreakdownChange
                    ? customBreakdownChange(key, option)
                    : handleBreakdownChange(key, option)
                }
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isHiresTableLoading ? (
        <Center minHeight="200px">
          <Spinner color="primary-800" size="sm" />
        </Center>
      ) : (
        <EmptyDataWrapper data={dataToUse} field="hires">
          <Table
            tableId="hire-table"
            skipLocalStorage={skipLocalStorage}
            defaultSortBy={[{ id: 'header', desc: false }]}
            isColumnLevelSorting
            enableColumnConfig={isEditMode}
            columns={columns}
            data={dataToUse}
            enableFooter={true}
            tableColStyles={tableColStyles}
            layoutScrollContainerStyles="c-data-table wide-content table-container"
            enableExpanded={true}
            layoutStyles={{
              borderBottom: 'none',
            }}
            getTableState={handleSortChange}
            onTableColDataChange={onTableColDataChange}
            initialTableColData={initialTableColData}
          />
        </EmptyDataWrapper>
      )}
    </Wrapper>
  );
};
